import { ref } from 'vue';
import { defineStore } from 'pinia';

export type WhatsNewView = 'entry' | 'list';

const SEEN_PREFIX = 'whats-new-seen:';

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function safeSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private window: the dot just shows again next time */ }
}

/**
 * UI state for What's new: the one drawer (mounted once in the admin layout,
 * opened from the desktop sidebar or the phone menu) and, per user, the
 * newest entry they have seen, which drives the dot on the menu.
 */
export const useWhatsNewStore = defineStore('whatsNew', () => {
  const drawerVisible = ref(false);
  const view = ref<WhatsNewView>('list');
  const selectedId = ref<string | null>(null);
  const seen = ref<Record<string, string>>({});

  function openEntry(id: string) {
    selectedId.value = id;
    view.value = 'entry';
    drawerVisible.value = true;
  }

  function openChangelog() {
    view.value = 'list';
    drawerVisible.value = true;
  }

  function close() {
    drawerVisible.value = false;
  }

  function seenFor(userId: string | null): string | null {
    if (!userId) return null;

    return seen.value[userId] ?? safeGet(SEEN_PREFIX + userId);
  }

  function markSeen(userId: string, entryId: string) {
    seen.value = { ...seen.value, [userId]: entryId };
    safeSet(SEEN_PREFIX + userId, entryId);
  }

  return { drawerVisible, view, selectedId, openEntry, openChangelog, close, seenFor, markSeen };
});
